import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Database, ShieldCheck, Zap, Download, ArrowRight, CheckCircle2, Building2, Sparkles, EyeOff } from "lucide-react";
import { useTheme } from "@/context/ThemeContext";
import { Moon, Sun } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import api from "@/lib/api";
import LiveCounter from "@/components/LiveCounter";
import { SampleDownloadCTA } from "@/components/SampleDownload";
import Seo from "@/components/Seo";
import WhatsAppFab, { WhatsAppButton } from "@/components/WhatsAppFab";
import { SITE, WA_MESSAGES, COVERAGE_FACTS, PRICING } from "@/lib/site";
import { FAQ_ITEMS } from "@/pages/public/Faq";

const features = [
  { icon: Database, title: "Fresh Daily Data", desc: "Newly registered GST & MCA companies delivered as separate daily datasets." },
  { icon: ShieldCheck, title: "Filtered By You", desc: "State, city, district or ROC — get only the geography that matters." },
  { icon: Zap, title: "Real-Time Uploads", desc: "Instant notification the moment new data lands in your dashboard." },
  { icon: Download, title: "Excel Ready", desc: "Download today's file or the entire 30-day period in one click." },
];

export default function Landing() {
  const { theme, toggle } = useTheme();
  const [sample, setSample] = useState(null);
  const [sampleTab, setSampleTab] = useState("gst");

  useEffect(() => {
    api.get("/public/sample").then((r) => setSample(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const homeJsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: SITE.brand,
      url: SITE.url,
      logo: `${SITE.url}/pwa-icon-512.png`,
      description: "India's daily feed of newly registered MCA and GST companies",
      sameAs: [],
      contactPoint: [{ "@type": "ContactPoint", contactType: "sales", email: SITE.salesEmail, telephone: "+91-8537942196", areaServed: "IN" }],
    },
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: SITE.brand,
      url: SITE.url,
      potentialAction: { "@type": "SearchAction", target: `${SITE.url}/?q={search_term_string}`, "query-input": "required name=search_term_string" },
    },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Seo
        title="MCA & GST Company Database India | Daily Fresh Leads | Registry Feed"
        description="Buy daily updated MCA and GST newly registered company database in India. Verified contacts, pan-India coverage, Excel/CSV delivery. Trial ₹3,000. WhatsApp: +91 8537942196"
        keywords="mca database india, gst data provider india, newly registered company data india, fresh company database india, new business leads india, company database india, daily company registration feed"
        path="/"
        jsonLd={homeJsonLd}
      />
      {/* Nav */}
      <header className="border-b border-border glass-header sticky top-0 z-50">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-md bg-primary text-primary-foreground grid place-items-center font-heading font-bold text-lg">R</div>
            <div className="leading-tight">
              <div className="font-heading font-bold text-lg">Registry<span className="text-primary">Feed</span></div>
              <div className="text-[10px] tracking-widest text-muted-foreground uppercase">GST · MCA Data</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <nav className="hidden md:flex items-center gap-1 text-sm mr-2">
              <Link to="/mca-database" className="px-3 py-1.5 rounded-md hover:bg-accent" data-testid="nav-mca">MCA</Link>
              <Link to="/gst-database" className="px-3 py-1.5 rounded-md hover:bg-accent" data-testid="nav-gst">GST</Link>
              <Link to="/new-company-database" className="px-3 py-1.5 rounded-md hover:bg-accent" data-testid="nav-new">New Companies</Link>
              <Link to="/pricing" className="px-3 py-1.5 rounded-md hover:bg-accent" data-testid="nav-pricing">Pricing</Link>
              <Link to="/faq" className="px-3 py-1.5 rounded-md hover:bg-accent" data-testid="nav-faq">FAQ</Link>
            </nav>
            <Button variant="ghost" size="icon" onClick={toggle} data-testid="landing-theme-toggle">
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
            <Link to="/login"><Button variant="ghost" data-testid="landing-login-btn">Log in</Button></Link>
            <Link to="/register"><Button className="btn-shine" data-testid="landing-signup-btn">Get started</Button></Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />
        <div className="relative mx-auto max-w-[1400px] px-4 sm:px-6 py-16 sm:py-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs text-muted-foreground">
              <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
              India's freshest company registry feed
            </div>
            <h1 className="mt-6 font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight">
              MCA &amp; GST Company Database.<br />
              <span className="text-primary">Newly registered businesses, every day.</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-2xl leading-relaxed">
              Registry Feed is India's daily feed of newly incorporated MCA companies and newly registered GST businesses — filter by state, city, district or ROC and download clean CSV exports the same day.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/mca-database"><Button size="lg" className="btn-shine" data-testid="hero-mca-btn">Get MCA Data <ArrowRight className="ml-2 h-4 w-4" /></Button></Link>
              <Link to="/gst-database"><Button size="lg" variant="outline" data-testid="hero-gst-btn">Get GST Data</Button></Link>
              <SampleDownloadCTA />
              <WhatsAppButton message={WA_MESSAGES.general} label="WhatsApp Sales" testid="hero-wa-btn" />
            </div>
            <ul className="mt-8 grid sm:grid-cols-2 gap-2 text-sm text-muted-foreground">
              {["30 day access per subscription", "GST + MCA in one platform", "No payment gateway — manual approval", "Data-only pricing, no fluff"].map((t) => (
                <li key={t} className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-primary mt-0.5" strokeWidth={2} /><span>{t}</span></li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-5">
            <div className="relative rounded-lg border border-border bg-card p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <div className="text-xs tracking-widest text-muted-foreground uppercase">Sample dataset</div>
                <div className="text-xs num text-muted-foreground">DD-MM-YYYY</div>
              </div>
              <div className="space-y-2">
                {[
                  { t: "GST", s: "Maharashtra — Mumbai", n: "612 rows" },
                  { t: "GST", s: "Karnataka — Bengaluru", n: "487 rows" },
                  { t: "MCA", s: "ROC Delhi I", n: "203 rows" },
                  { t: "MCA", s: "Tamil Nadu — Chennai", n: "156 rows" },
                ].map((r) => (
                  <div key={`${r.t}-${r.s}`} className="flex items-center justify-between rounded-md border border-border px-3 py-2.5 hover:border-primary/50 transition-colors">
                    <div className="flex items-center gap-3">
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${r.t === "GST" ? "bg-primary/10 text-primary" : "bg-emerald-500/10 text-emerald-500"}`}>{r.t}</span>
                      <div>
                        <div className="text-sm font-medium">{r.s}</div>
                        <div className="text-[11px] text-muted-foreground">Filtered feed</div>
                      </div>
                    </div>
                    <div className="text-sm num font-semibold">{r.n}</div>
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-4 border-t border-border flex items-center justify-between text-xs">
                <div className="text-muted-foreground">Auto-updated when admin uploads</div>
                <Building2 className="h-4 w-4 text-primary" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <LiveCounter />

      {/* Keyword-rich intro for SEO — additive only */}
      <section className="mx-auto max-w-[1400px] px-4 sm:px-6 py-6">
        <p className="text-sm sm:text-base text-muted-foreground leading-relaxed max-w-3xl">
          Registry Feed is India's most trusted daily feed of newly registered MCA companies and GST businesses. Whether you need fresh MCA database, new GST registration data, or combined new company leads — we deliver verified, filtered data every single day across all 36 states and union territories.
        </p>
      </section>

      {/* Features */}
      <section className="border-t border-border">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 py-16 sm:py-20">
          <div className="max-w-2xl">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight">Built for lead teams that ship every day</h2>
            <p className="mt-3 text-muted-foreground">A no-nonsense data feed and dashboard. Choose your package, get approved, download every day.</p>
          </div>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {features.map((f) => (
              <div key={f.title} className="rounded-lg border border-border bg-card p-5 hover:-translate-y-1 hover:shadow-sm transition-all">
                <div className="h-9 w-9 rounded-md bg-primary/10 text-primary grid place-items-center">
                  <f.icon className="h-5 w-5" strokeWidth={1.75} />
                </div>
                <div className="mt-4 font-heading font-semibold">{f.title}</div>
                <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sample dataset */}
      <section className="border-t border-border bg-muted/30" data-testid="sample-section">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 py-16 sm:py-20">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs text-muted-foreground">
              <Sparkles className="h-3 w-3 text-primary" /> Live sample
            </div>
            <h2 className="mt-3 font-heading font-bold text-3xl sm:text-4xl tracking-tight">See real rows from today's feed</h2>
            <p className="mt-3 text-muted-foreground">A tiny redacted slice from the latest upload. Personal contact details are masked. <Link to="/register" className="text-primary hover:underline">Sign up</Link> to unlock full records.</p>
          </div>

          <div className="mt-8 rounded-lg border border-border bg-card overflow-hidden">
            <Tabs value={sampleTab} onValueChange={setSampleTab}>
              <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                <TabsList data-testid="sample-tabs">
                  <TabsTrigger value="gst" data-testid="sample-tab-gst">GST</TabsTrigger>
                  <TabsTrigger value="mca" data-testid="sample-tab-mca">MCA</TabsTrigger>
                </TabsList>
                {sample && (
                  <div className="text-xs text-muted-foreground flex items-center gap-3">
                    <span className="hidden sm:inline-flex items-center gap-1"><EyeOff className="h-3 w-3" /> PII masked</span>
                    <span className="num">
                      {sample[sampleTab]?.upload_date ? new Date(sample[sampleTab].upload_date).toLocaleDateString() : "—"}
                    </span>
                  </div>
                )}
              </div>

              <TabsContent value="gst" className="m-0">
                <SampleTable data={sample?.gst} testid="sample-gst-table" />
              </TabsContent>
              <TabsContent value="mca" className="m-0">
                <SampleTable data={sample?.mca} testid="sample-mca-table" />
              </TabsContent>
            </Tabs>
          </div>

          <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
            <div className="text-sm text-muted-foreground">
              {sample ? (
                <>Preview shows {sample[sampleTab]?.rows?.length ?? 0} of <span className="num font-medium text-foreground">{(sample[sampleTab]?.total_available ?? 0).toLocaleString()}</span> records in today's file.</>
              ) : (
                <>Loading today's feed sample…</>
              )}
            </div>
            <Link to="/register"><Button className="btn-shine" data-testid="sample-signup-btn">Sign up to unlock full data <ArrowRight className="ml-2 h-4 w-4" /></Button></Link>
          </div>
        </div>
      </section>

      {/* Data streams overview — three commercial entry points */}
      <section className="border-t border-border">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 py-16 sm:py-20">
          <div className="max-w-2xl">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight">Two data streams. One platform.</h2>
            <p className="mt-3 text-muted-foreground">Pick MCA, GST or both — Pan India or a specific state / city / district / ROC.</p>
          </div>
          <div className="mt-10 grid md:grid-cols-3 gap-4">
            <Link to="/mca-database" className="rounded-lg border border-border bg-card p-6 hover:-translate-y-1 hover:shadow-sm transition-all" data-testid="home-card-mca">
              <div className="text-xs uppercase tracking-widest text-muted-foreground">MCA Company Database</div>
              <div className="mt-3 font-heading font-bold text-xl">Newly incorporated companies</div>
              <p className="mt-2 text-sm text-muted-foreground">Fresh Pvt Ltd, LLP, OPC and Public companies from every ROC jurisdiction.</p>
              <div className="mt-4 text-sm text-primary inline-flex items-center gap-1">Explore <ArrowRight className="h-4 w-4" /></div>
            </Link>
            <Link to="/gst-database" className="rounded-lg border border-border bg-card p-6 hover:-translate-y-1 hover:shadow-sm transition-all" data-testid="home-card-gst">
              <div className="text-xs uppercase tracking-widest text-muted-foreground">GST Company Database</div>
              <div className="mt-3 font-heading font-bold text-xl">New GST registrations</div>
              <p className="mt-2 text-sm text-muted-foreground">Freshly registered proprietorships, partnerships and companies, filtered by state or city.</p>
              <div className="mt-4 text-sm text-primary inline-flex items-center gap-1">Explore <ArrowRight className="h-4 w-4" /></div>
            </Link>
            <Link to="/new-company-database" className="rounded-lg border border-border bg-card p-6 hover:-translate-y-1 hover:shadow-sm transition-all" data-testid="home-card-new">
              <div className="text-xs uppercase tracking-widest text-muted-foreground">New Company Database</div>
              <div className="mt-3 font-heading font-bold text-xl">MCA + GST combined</div>
              <p className="mt-2 text-sm text-muted-foreground">Both streams in one subscription — the fastest path from registration to outreach.</p>
              <div className="mt-4 text-sm text-primary inline-flex items-center gap-1">Explore <ArrowRight className="h-4 w-4" /></div>
            </Link>
          </div>
        </div>
      </section>

      {/* Coverage strip */}
      <section className="border-t border-border bg-muted/30">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 py-14">
          <div className="max-w-2xl">
            <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight">Coverage &amp; delivery you can plan around</h2>
          </div>
          <ul className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-2 text-sm">
            {COVERAGE_FACTS.map((c) => (
              <li key={c} className="flex items-start gap-2 rounded-md border border-border bg-card px-3 py-2.5"><CheckCircle2 className="h-4 w-4 text-primary mt-0.5" /> {c}</li>
            ))}
          </ul>
          <div className="mt-6 text-sm"><Link to="/data-coverage" className="text-primary hover:underline">See full coverage details →</Link></div>
        </div>
      </section>

      {/* Target businesses */}
      <section className="border-t border-border">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 py-14">
          <div className="max-w-2xl">
            <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight">Built for B2B teams that sell to new businesses</h2>
            <p className="mt-3 text-muted-foreground">If you sell to newly registered companies, Registry Feed is your fresh pipeline every day.</p>
          </div>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
            {["CAs & Tax Consultants", "Banks · NBFCs · DSAs", "B2B SaaS & FinTech", "Wholesalers & Distributors", "Insurance Providers", "Legal & Compliance Firms", "Marketing Agencies", "Logistics & Supply Chain"].map((t) => (
              <div key={t} className="rounded-md border border-border bg-card px-3 py-3 text-center">{t}</div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing preview */}
      <section className="border-t border-border bg-muted/30">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 py-14">
          <div className="max-w-2xl">
            <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight">Simple, honest pricing</h2>
            <p className="mt-3 text-muted-foreground">Pay per data stream, per month. Try first with a 3-day Pan India trial.</p>
          </div>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: "Trial (3 days)", price: `₹${PRICING.trial.price.toLocaleString("en-IN")}`, note: "Pan India" },
              { label: "MCA", price: `₹${PRICING.mca.price.toLocaleString("en-IN")}`, note: "/ month" },
              { label: "GST", price: `₹${PRICING.gst.price.toLocaleString("en-IN")}`, note: "/ month" },
              { label: "State-wise", price: "Custom", note: "WhatsApp us" },
            ].map((t) => (
              <div key={t.label} className="rounded-lg border border-border bg-card p-5">
                <div className="text-xs uppercase tracking-widest text-muted-foreground">{t.label}</div>
                <div className="mt-3 text-2xl font-heading font-black num">{t.price}</div>
                <div className="text-sm text-muted-foreground">{t.note}</div>
              </div>
            ))}
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/pricing"><Button className="btn-shine">See Full Pricing <ArrowRight className="ml-2 h-4 w-4" /></Button></Link>
            <WhatsAppButton message={WA_MESSAGES.general} label="Ask about pricing" testid="home-pricing-wa" />
          </div>
        </div>
      </section>

      {/* Homepage FAQ (5 top items) */}
      <section className="border-t border-border">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 py-14">
          <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight">Common questions</h2>
          <Accordion type="single" collapsible className="mt-6" data-testid="home-faq">
            {FAQ_ITEMS.slice(0, 5).map((f, i) => (
              <AccordionItem key={f.q} value={`h-${i}`}>
                <AccordionTrigger className="text-left">{f.q}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <div className="mt-6 text-sm"><Link to="/faq" className="text-primary hover:underline">See all FAQs →</Link></div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="border-t border-border bg-primary/5">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 py-14 text-center">
          <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight">Start reaching new companies today.</h2>
          <p className="mt-3 text-muted-foreground max-w-xl mx-auto">Talk to us on WhatsApp, or request a sample and see the data quality yourself.</p>
          <div className="mt-6 flex flex-wrap gap-3 justify-center">
            <WhatsAppButton message={WA_MESSAGES.general} label="WhatsApp Sales" testid="final-wa-btn" />
            <SampleDownloadCTA />
            <Link to="/pricing"><Button size="lg" variant="outline">See Pricing</Button></Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-border bg-muted/20 mt-4">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 py-12 grid grid-cols-2 sm:grid-cols-4 gap-8 text-sm">
          <div>
            <div className="font-heading font-bold mb-3">Products</div>
            <ul className="space-y-2 text-muted-foreground">
              <li><Link to="/mca-database" className="hover:text-foreground">MCA Company Database</Link></li>
              <li><Link to="/gst-database" className="hover:text-foreground">GST Company Database</Link></li>
              <li><Link to="/new-company-database" className="hover:text-foreground">New Company Data</Link></li>
              <li><Link to="/mca-gst-database" className="hover:text-foreground">MCA + GST Combo</Link></li>
              <li><Link to="/sample-data" className="hover:text-foreground">Sample Data</Link></li>
            </ul>
          </div>
          <div>
            <div className="font-heading font-bold mb-3">Company</div>
            <ul className="space-y-2 text-muted-foreground">
              <li><Link to="/about" className="hover:text-foreground">About Us</Link></li>
              <li><Link to="/why-registryfeed" className="hover:text-foreground">Why Registry Feed</Link></li>
              <li><Link to="/how-it-works" className="hover:text-foreground">How It Works</Link></li>
              <li><Link to="/data-coverage" className="hover:text-foreground">Data Coverage</Link></li>
              <li><Link to="/faq" className="hover:text-foreground">FAQ</Link></li>
            </ul>
          </div>
          <div>
            <div className="font-heading font-bold mb-3">Contact</div>
            <ul className="space-y-2 text-muted-foreground">
              <li><Link to="/contact" className="hover:text-foreground">Contact / Get Quote</Link></li>
              <li><Link to="/pricing" className="hover:text-foreground">Pricing</Link></li>
              <li><a href={`mailto:${SITE.salesEmail}`} className="hover:text-foreground">{SITE.salesEmail}</a></li>
              <li><span className="num">{SITE.whatsappDisplay}</span></li>
            </ul>
          </div>
          <div>
            <div className="font-heading font-bold mb-3">Legal</div>
            <ul className="space-y-2 text-muted-foreground">
              <li><Link to="/privacy" className="hover:text-foreground">Privacy Policy</Link></li>
              <li><Link to="/terms" className="hover:text-foreground">Terms &amp; Conditions</Link></li>
              <li><Link to="/refund-policy" className="hover:text-foreground">Refund Policy</Link></li>
              <li><Link to="/disclaimer" className="hover:text-foreground">Disclaimer</Link></li>
              <li><Link to="/acceptable-use" className="hover:text-foreground">Acceptable Use</Link></li>
              <li><Link to="/cookie-policy" className="hover:text-foreground">Cookie Policy</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-border">
          <div className="mx-auto max-w-[1400px] px-4 sm:px-6 py-5">
            <div className="text-xs text-muted-foreground mb-3">
              <span className="font-medium text-foreground/70">Popular Searches</span>
              <div className="mt-2 flex flex-wrap gap-x-1 gap-y-1.5">
                {[
                  ["MCA Company Database", "/mca-database"],
                  ["GST Database India", "/gst-database"],
                  ["Newly Registered Company Data", "/new-company-database"],
                  ["Buy MCA Database", "/mca-database"],
                  ["Fresh Business Leads India", "/new-company-database"],
                  ["New GST Registration List", "/gst-database"],
                  ["Company Database India", "/"],
                  ["MCA Data Provider", "/mca-database"],
                  ["GST Data Provider India", "/gst-database"],
                  ["New Company Leads", "/new-company-database"],
                ].map(([label, to], i) => (
                  <React.Fragment key={label}>
                    {i > 0 && <span className="mx-1 text-border">|</span>}
                    <Link to={to} className="hover:text-foreground transition-colors">{label}</Link>
                  </React.Fragment>
                ))}
              </div>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
              <div>© {new Date().getFullYear()} {SITE.brand}. Newly registered company data for B2B teams.</div>
              <div className="num">India · GST · MCA · Daily feed</div>
            </div>
          </div>
        </div>
      </footer>
      <WhatsAppFab />
    </div>
  );
}

function SampleTable({ data, testid }) {
  const rows = data?.rows || [];
  const cols = data?.columns || [];
  if (!data || rows.length === 0) {
    return (
      <div className="p-10 text-center text-sm text-muted-foreground" data-testid={`${testid}-empty`}>
        No sample records available yet.
      </div>
    );
  }
  // Show only a manageable subset of columns for the sample display
  const displayCols = cols.slice(0, 6);
  return (
    <div className="overflow-x-auto" data-testid={testid}>
      <table className="w-full text-xs">
        <thead className="bg-muted/40 text-[10px] uppercase tracking-widest text-muted-foreground">
          <tr>
            {displayCols.map((c) => (
              <th key={c} className="text-left px-4 py-2.5 whitespace-nowrap">{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.id || r.GSTIN || r.CIN || `row-${i}`} className="border-t border-border hover:bg-accent/40 transition-colors">
              {displayCols.map((c) => (
                <td key={c} className="px-4 py-2.5 whitespace-nowrap text-muted-foreground num">
                  {String(r[c] ?? "")}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}